import type { EquipmentSnapshot, TerminalCapabilities } from './types'

/** Сохранённые терминалы со статусом связи (п. 3.5). Слова вместо цвета: состояние — значок и слово. */

function capabilityNote(capabilities: TerminalCapabilities): string | null {
  const missing = [
    capabilities.cancel ? null : 'отмену',
    capabilities.refund ? null : 'возврат',
    capabilities.reconcile ? null : 'сверку итогов',
  ].filter((word): word is string => word !== null)
  const parts: string[] = []
  if (missing.length > 0) parts.push(`Не умеет: ${missing.join(', ')}.`)
  if (!capabilities.verified) parts.push('Отмена и возврат пока не проверены на живом терминале.')
  return parts.length === 0 ? null : parts.join(' ')
}

// ---- окно «Оборудование», версия 2 (EQW2-W0): сохранённые терминалы со статусом связи ----

/** Состояние связи сохранённого терминала. «Не проверялась» — отдельное слово, не «готов» (п. 3.5, 9.4). */
export type TerminalLinkState = 'unchecked' | 'ok' | 'failed'

export interface TerminalCardVm {
  id: string
  title: string
  /** Основной (им платят по умолчанию). */
  primary: boolean
  details: readonly string[]
  linkState: TerminalLinkState
  linkWord: string
  linkGlyph: '…' | '✓' | '✕'
  /** Текст проверки дословно; для «не проверялась» — подсказка нажать «Проверить связь». */
  linkText: string
  checkedAt: number | null
  capabilityNote: string | null
  /** Умеет: оплата, отмена, возврат… */
  abilities: string
  canMakePrimary: boolean
}

function abilitiesText(capabilities: TerminalCapabilities): string {
  const words = [
    capabilities.pay ? 'оплата' : null,
    capabilities.cancel ? 'отмена' : null,
    capabilities.refund ? 'возврат' : null,
    capabilities.reconcile ? 'сверка итогов' : null,
  ].filter((word): word is string => word !== null)
  return words.length === 0 ? 'ничего не умеет' : words.join(', ')
}

/** Карточки настроенных терминалов; пустой список — терминалов нет или агент о них не сообщает. */
export function terminalCards(snapshot: Pick<EquipmentSnapshot, 'terminals' | 'terminalChecks'>): readonly TerminalCardVm[] {
  const data = snapshot.terminals
  if (data === null) return []
  return data.items.map((item): TerminalCardVm => {
    const check = snapshot.terminalChecks[item.id]
    const driver = data.drivers.find((candidate) => candidate.kind === item.kind)
    const details = (driver?.fields ?? [])
      .map((field) => ({ title: field.title, value: item.values[field.key] }))
      .filter((entry): entry is { title: string; value: string } => entry.value !== undefined && entry.value.length > 0)
      .map((entry) => `${entry.title}: ${entry.value}`)
    const linkState: TerminalLinkState = check === undefined ? 'unchecked' : check.ok ? 'ok' : 'failed'
    return {
      id: item.id,
      title: item.title,
      primary: item.active,
      details,
      linkState,
      linkWord: linkState === 'unchecked' ? 'Связь не проверялась' : linkState === 'ok' ? 'Связь есть' : 'Нет связи',
      linkGlyph: linkState === 'unchecked' ? '…' : linkState === 'ok' ? '✓' : '✕',
      linkText: check?.message ?? 'Нажмите «Проверить связь».',
      checkedAt: check?.checkedAt ?? null,
      capabilityNote: capabilityNote(item.capabilities),
      abilities: abilitiesText(item.capabilities),
      canMakePrimary: !item.active,
    }
  })
}
